// ============================================================
// Attendance Scanner Modal — Live Camera QR Code Scanner
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, IconButton, Alert, CircularProgress, Paper } from '@mui/material';
import { X, QrCode, CheckCircle2, AlertCircle, Camera, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Html5Qrcode } from 'html5-qrcode';
import { attendanceApi } from '@/api/attendance.api';
import { useSchool } from '@/hooks/useSchool';
import { useAuth } from '@/hooks/useAuth';
import { tokens } from '@/theme/tokens';

export function AttendanceScannerModal({ open, onClose }) {
  const { user } = useAuth();
  const userRole = String(user?.role || '').toLowerCase();
  const isOrgAdmin = userRole === 'organization_admin' || userRole === 'super_admin';

  const { currentSchool } = useSchool();
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  const html5QrCodeRef = useRef(null);
  const hasScannedRef = useRef(false);

  // Stop camera hardware completely
  const stopCameraScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const instance = html5QrCodeRef.current;
        html5QrCodeRef.current = null;
        if (instance.isScanning) {
          await instance.stop();
        }
        await instance.clear();
      } catch {
        // Ignore stop errors
      } finally {
        setCameraActive(false);
      }
    }
  }, []);

  // Submit single scan payload to backend
  const submitAttendance = useCallback(async (scannedText) => {
    if (!currentSchool?.id || !scannedText) return;

    let signature = '';
    let type = 'STATIC';
    let targetSchoolId = currentSchool?.id;
    let timestamp = Date.now();

    try {
      const parsed = JSON.parse(scannedText);
      if (parsed.school_id) targetSchoolId = parsed.school_id;
      if (parsed.signature) signature = String(parsed.signature);
      else if (parsed.token) signature = String(parsed.token);
      else if (parsed.qr_secret) signature = String(parsed.qr_secret);
      else if (parsed.code) signature = String(parsed.code);

      if (parsed.type) type = parsed.type;
      if (parsed.qr_type) type = parsed.qr_type;
      if (parsed.timestamp) timestamp = Number(parsed.timestamp) || timestamp;
    } catch {
      // Scanned text is a raw string
      signature = String(scannedText);
    }

    if (!signature) {
      toast.error('Invalid QR code scanned');
      return;
    }

    try {
      setLoading(true);
      setScanResult(null);

      const payload = {
        school_id: targetSchoolId,
        type: type,
        timestamp: timestamp,
        signature: signature,
      };

      const res = await attendanceApi.scanQr(targetSchoolId, payload);
      const successMessage = res?.message || 'Attendance marked successfully!';
      setScanResult({ success: true, message: successMessage });
      toast.success(successMessage);

      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (error) {
      const errorMessage =
        (typeof error.response?.data?.detail === 'string' ? error.response?.data?.detail : null) ||
        error.response?.data?.message ||
        error.message ||
        'Failed to mark attendance';
      setScanResult({ success: false, message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentSchool?.id, onClose]);

  // Start camera scanner
  const startCameraScanner = useCallback(async () => {
    setCameraError(null);
    setCameraActive(false);
    setScanResult(null);
    hasScannedRef.current = false;

    // Wait for DOM element render
    await new Promise((resolve) => setTimeout(resolve, 200));

    const element = document.getElementById('topbar-qr-reader');
    if (!element) return;

    try {
      await stopCameraScanner();

      const html5QrCode = new Html5Qrcode('topbar-qr-reader');
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 220, height: 220 },
        aspectRatio: 1.0,
      };

      setCameraActive(true);

      await html5QrCode.start(
        { facingMode: 'environment' },
        config,
        async (decodedText) => {
          // Strictly lock to single scan attempt
          if (hasScannedRef.current) return;
          hasScannedRef.current = true;

          // STOP camera hardware immediately before starting API call
          await stopCameraScanner();

          // Execute single API call
          await submitAttendance(decodedText);
        },
        () => {
          // Frame scan error (ignore noise)
        }
      );
    } catch (err) {
      console.warn('Camera initialization failed:', err);
      setCameraActive(false);
      setCameraError('Camera access unavailable or permission denied.');
    }
  }, [stopCameraScanner, submitAttendance]);

  useEffect(() => {
    if (open) {
      startCameraScanner();
    } else {
      stopCameraScanner();
    }

    return () => {
      stopCameraScanner();
    };
  }, [open, startCameraScanner, stopCameraScanner]);

  if (isOrgAdmin) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        stopCameraScanner();
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: tokens.colors.primary[50],
              color: tokens.colors.primary[600],
              display: 'flex',
            }}
          >
            <QrCode size={22} />
          </Box>
          <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
            Scan Attendance QR
          </Typography>
        </Box>
        <IconButton
          onClick={() => {
            stopCameraScanner();
            onClose();
          }}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        {scanResult ? (
          <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {scanResult.success ? (
              <CheckCircle2 size={64} color={tokens.colors.success[500]} />
            ) : (
              <AlertCircle size={64} color={tokens.colors.error[500]} />
            )}
            <Typography variant="h6" fontWeight={600}>
              {scanResult.success ? 'Success!' : 'Scan Failed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scanResult.message}
            </Typography>
            {!scanResult.success && (
              <Button
                variant="contained"
                startIcon={<RefreshCw size={16} />}
                onClick={startCameraScanner}
                size="small"
                sx={{ mt: 1 }}
              >
                Scan Again
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Live Camera Scanner Box */}
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                aspectRatio: '1',
                maxHeight: 280,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                bgcolor: 'grey.900',
                border: '2px solid',
                borderColor: cameraActive ? 'primary.main' : 'divider',
              }}
            >
              <div id="topbar-qr-reader" style={{ width: '100%', height: '100%' }} />

              {/* Scanning Overlay Animation */}
              {cameraActive && !loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      border: '2px dashed rgba(255, 255, 255, 0.7)',
                      borderRadius: 2,
                      position: 'relative',
                    }}
                  />
                </Box>
              )}

              {/* Loading Spinner during API call */}
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.65)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'common.white',
                    gap: 1.5,
                    zIndex: 10,
                  }}
                >
                  <CircularProgress size={36} color="primary" />
                  <Typography variant="body2" fontWeight={600}>
                    Verifying Attendance...
                  </Typography>
                </Box>
              )}
            </Paper>

            {cameraError ? (
              <Alert severity="error" sx={{ width: '100%', mt: 1 }}>
                {cameraError}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Camera size={16} />
                Point your camera at the Attendance QR code
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
        {cameraError && (
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={16} />}
            onClick={startCameraScanner}
            size="small"
          >
            Retry Camera
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
